import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { CommonModule, ViewportScroller } from '@angular/common';
import { ProjectService } from '../../services/project.service';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { Project } from '../../models/project';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { LabTest } from '../../models/lab-test';
import { PVT_CONSTANTS, roleIds } from '../../common/constants';
import { cceDPConstants, PROJECT_CONSTANTS } from '../../common/experiment-constants';
import { GeoPoliticalEntity } from '../../models/osdu';
import { OsduService } from '../../services/osdu.service';
import { SharedService } from '../../services/shared.service';
import { Subject, takeUntil } from 'rxjs';
import { MESSAGES_CONSTANTS as MC } from '../../common/messages-constants';
import { NUMBERS_CONSTANTS } from '../../common/numbers-constants';
import { LoadingService } from '../../services/loading.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';

@Component({
  selector: 'app-create-project',
  templateUrl: './create-project.component.html',
  styleUrl: './create-project.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule
  ]
})
export class CreateProjectComponent implements OnInit, OnDestroy {
  readonly roleIds = roleIds;
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private projectService = inject(ProjectService);
  private toastr = inject(ToastrService);
  private dialog = inject(MatDialog);
  private _osduService = inject(OsduService);
  private sharedService = inject(SharedService);
  private viewportScroller = inject(ViewportScroller);
  private loadingService = inject(LoadingService);

  userId = '';
  userName = '';
  roleId: string | undefined;
  addProjectForm!: FormGroup;
  countries: string[] = [];
  selectedCountry = '';
  errorMessage = "";
  successMessage = "";
  projectDetails!: Project;
  fileNames: string[] = [];
  firstFileName = "";
  allFiles: File[] = [];
  projectId = 0;
  labTestDetail: LabTest | undefined = undefined;
  countriesDetails: GeoPoliticalEntity[] = [];
  selectedCountryDetails: GeoPoliticalEntity | undefined;
  private readonly destroy$ = new Subject<void>();
  @ViewChild('filesDialog') filesDialog!: TemplateRef<unknown>;
  invalidFieldIds = new Set<string>();

  ngOnInit(): void {
    this.loadingService.ShowLoadingSpinner();
    this.viewportScroller.scrollToPosition([0, 0]);

    this.sharedService.userDetail$.pipe(takeUntil(this.destroy$)).subscribe((userDetail) => {
      if (userDetail) {
        this.userId = userDetail?.mail;
        this.userName = userDetail?.displayName;
      }
    });
    this.sharedService.userRoleId$.pipe(takeUntil(this.destroy$)).subscribe((userRoleId) => {
      if (userRoleId) {
        this.roleId = userRoleId;
      }
    });

    this.projectId = Number(
      this.router.routerState.snapshot.root.firstChild?.paramMap.get(PROJECT_CONSTANTS.projectId) ??
      this.router.routerState.snapshot.root.firstChild?.paramMap.get(PROJECT_CONSTANTS.id)
    ) || 0;

    this.addProjectForm = this.fb.group({
      projectNumber: ['', Validators.required],
      projectName: [''],
      jobNumber: [''],
      leadEngineer: [''],
      buContact: [''],
      files: [null],
      field: [''],
      country: ['', Validators.required],
      kickOffMeetingDate: [''],
      sampleReceivedDate: [''],
      restorationStartedDate: [''],
      workCompletedDate: [''],
      reportSubmittedDate: [''],
    });

    this.GetCountries();


    // Set projectId from the route (expects a route param named 'id' or 'projectId')
    this.f[PROJECT_CONSTANTS.country]?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(v => this.selectedCountry = v || '');
  }


  get f(): Record<string, AbstractControl> {
    return this.addProjectForm.controls;
  }

  GetCountries() {
    this._osduService.GetGeoPoliticalEntities().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res && res.data) {
          this.countriesDetails = res.data;

          this.countriesDetails.sort((a, b) =>
            a.geoPoliticalEntityName.localeCompare(b.geoPoliticalEntityName)
          );

          this.countries = res.data.map(n => n.geoPoliticalEntityName);
          this.GetProjectDetails();
        }
      },
      error: () => {
        this.GetProjectDetails();
      },
    });
  }

  GetProjectDetails() {
    if (this.projectId > 0) {
      this.projectService.GetProjectInfo(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
        next: res => {
          if (res.isSuccess && res.result) {
            this.projectDetails = res.result;

            this.addProjectForm.patchValue(this.projectDetails);
            this.addProjectForm.updateValueAndValidity();

            // Disable country field in edit mode
            this.f[PROJECT_CONSTANTS.country]?.disable();

            this.fileNames = res.result.files?.split(',').map((f: string) => f.trim()).filter((f: string) => f) || [];
            this.firstFileName = this.fileNames[0] || '';

            // Set selectedCountry from loaded project
            this.selectedCountry = res.result.country || '';
            this.projectService.SetCountry(this.selectedCountry);
          }
          this.loadingService.HideLoadingSpinner();
        },
        error: () => {
          this.loadingService.HideLoadingSpinner();
        }
      });
    }
    else {
      this.loadingService.HideLoadingSpinner();
    }

    this.f[PROJECT_CONSTANTS.country]?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(v => this.selectedCountry = v || '');
  }

  AddProject(event: Event) {
    event.stopPropagation();

    this.projectDetails = this.projectService.BuildProjectDetails({
      projectId: this.projectId,
      projectNumber: this.GetValue(PROJECT_CONSTANTS.projectNumber),
      projectName: this.GetValue(PROJECT_CONSTANTS.projectName),
      jobNumber: this.GetValue(PROJECT_CONSTANTS.jobNumber),
      leadEngineer: this.GetValue(PROJECT_CONSTANTS.leadEngineer),
      buContact: this.GetValue(PROJECT_CONSTANTS.buContact),
      fileNames: this.fileNames,
      field: this.GetValue(PROJECT_CONSTANTS.field),
      country: this.selectedCountry,
      kickOffMeetingDate: this.GetValue(PROJECT_CONSTANTS.kickOffMeetingDate),
      sampleReceivedDate: this.GetValue(PROJECT_CONSTANTS.sampleReceivedDate),
      restorationStartedDate: this.GetValue(PROJECT_CONSTANTS.restorationStartedDate),
      workCompletedDate: this.GetValue(PROJECT_CONSTANTS.workCompletedDate),
      reportSubmittedDate: this.GetValue(PROJECT_CONSTANTS.reportSubmittedDate),
      userId: this.userId,
      userName: this.userName,
      allFiles: this.allFiles
    });
    const validation = this.Validation(this.projectDetails);

    //Skip dialog for new projects
    if (validation.status === true) {
      // Set geoPoliticalEntityId based on selected country before saving/navigating
      if (this.selectedCountry && this.countriesDetails.length > 0) {
        this.selectedCountryDetails = this.countriesDetails.find(
          c => c.geoPoliticalEntityName === this.selectedCountry
        );

        if (this.selectedCountryDetails?.geoPoliticalEntityId) {
          this.projectService.SetGeoPoliticalEntityId(this.selectedCountryDetails.geoPoliticalEntityId);
        } else {
          console.warn(MC.couldNotFindGeoPoliticalEntityIdForSelectedCountry, this.selectedCountry);
        }
      }

      // For both new and existing projects, save immediately without dialog
      this.CallAddProjectApi();
    }
    else {
      this.errorMessage = MC.enterTheRequiredInformation;
      this.toastr.error(this.errorMessage);
    }
  }

  private CallAddProjectApi() {
    // Capture the initial state before API changes projectId
    const isNewProject = this.projectId === 0;

    const formData = this.projectService.BuildFormData(this.projectDetails, this.allFiles);
    if (isNewProject) {
      this.projectService.IsProjectNumberExists(this.projectDetails.projectNumber || '').pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          if (res.result) {
            this.toastr.error(MC.theProjectNumberAlreadyExists);
          }
          else {
            this.projectService.UpdateIsNewProject(isNewProject);
            this.projectService.UpdateProjectFormData(this.projectDetails);
            this.projectService.UpdateProjectFiles(this.allFiles);
            const navigationName = this.projectDetails.projectNumber ?? '';
            this.sharedService.NavigateToCreateSample(0, navigationName);
          }
        });
    }
    else {
      this.projectService.AddProject(formData).pipe(takeUntil(this.destroy$)).subscribe(
        async (res) => {
          if (res.isSuccess === true) {
            this.successMessage = PROJECT_CONSTANTS.saveSuccessMessage(isNewProject);
            this.toastr.success(this.successMessage);
            const projectDetails = res.result as Project;

            if (projectDetails) {
              this.addProjectForm.patchValue({
                projectNumber: projectDetails.projectNumber,
                projectName: projectDetails.projectName,
                jobNumber: projectDetails.jobNumber,
                leadEngineer: projectDetails.leadEngineer,
                buContact: projectDetails.buContact,
                field: projectDetails.field,
                country: projectDetails.country,
                kickOffMeetingDate: projectDetails.kickOffMeetingDate,
                sampleReceivedDate: projectDetails.sampleReceivedDate,
                restorationStartedDate: projectDetails.restorationStartedDate,
                workCompletedDate: projectDetails.workCompletedDate,
                reportSubmittedDate: projectDetails.reportSubmittedDate,
                files: null,
                status: projectDetails.status
              });

              this.projectId = projectDetails.projectId;
              this.selectedCountry = projectDetails.country || '';
              this.fileNames = (projectDetails.files || '').split(',').map(f => f.trim()).filter(f => f);
              this.firstFileName = this.fileNames[0] || '';
              this.addProjectForm.updateValueAndValidity();

              // Use captured state for navigation decision
              if (isNewProject) {
                // New project - navigate directly to sample list
                // Use project name if available, otherwise fall back to project number
                const navigationName = projectDetails.projectName || (projectDetails.projectNumber ?? '');
                this.sharedService.NavigateToCreateSample(projectDetails.projectId, navigationName);
              }
            }
          }
          else {
            if (res.errors.find(error => error.description === MC.duplicateValueFound)) {
              this.errorMessage = MC.theProjectNumberAlreadyExists;
            }
            else {
              this.errorMessage = MC.failedToCreateThe + PVT_CONSTANTS.Project;
            }
            this.toastr.error(this.errorMessage);
          }
        }
      );
    }

  }

  GetValue(controlName: string) {
    return this.addProjectForm.controls[controlName].value
  }

  ViewFiles() {
    this.dialog.open(this.filesDialog, {
      width: cceDPConstants.dialogWidth,
      disableClose: true
    });
  }

  OnFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > NUMBERS_CONSTANTS.zero) {
      const newFiles = Array.from(input.files);
      this.allFiles.push(...newFiles);
      this.fileNames.push(...newFiles.map(file => file.name));
      this.firstFileName = this.fileNames[0];
      const dataTransfer = new DataTransfer();
      this.allFiles.forEach(file => dataTransfer.items.add(file));
      this.addProjectForm.patchValue({ files: dataTransfer.files });
      this.setFilesOnForm(this.allFiles);
      input.value = '';
    }
  }

  RemoveFile(index: number) {
    this.allFiles.splice(index, 1);
    this.fileNames.splice(index, 1);
    this.firstFileName = this.fileNames[0];
    const dataTransfer = new DataTransfer();
    this.allFiles.forEach(file => dataTransfer.items.add(file));
    this.addProjectForm.patchValue({ files: dataTransfer.files });
    this.setFilesOnForm(this.allFiles);
  }


  private setFilesOnForm(files: File[]): void {
    const ctrl = this.addProjectForm.get('files');
    if (!ctrl) return;

    ctrl.setValue(null);
    ctrl.markAsPristine();

    const dt = new DataTransfer();
    files.forEach(f => dt.items.add(f));

    ctrl.setValue(dt.files);
    ctrl.updateValueAndValidity();
  }


  Validation(projectInfo: Project) {
    const result = this.projectService.ValidateProject(projectInfo);
    if (!result.status) {
      result.invalidFields.forEach(field => {
        this.AddValidation(field);
        if (field === PROJECT_CONSTANTS.projectNumber) {
          this.toastr.error(MC.enterProjectNumber);
        }
        if (field === PROJECT_CONSTANTS.country) {
          this.toastr.error(MC.enterCountry);
        }
      });
      return { status: false };
    }
    return { status: true, error: '' };
  }

  RemoveValidation(id: string) {
    this.invalidFieldIds.delete(id);
  }

  AddValidation(id: string) {
    this.invalidFieldIds.add(id);
  }

  GoToLabList() {
    // Set geoPoliticalEntityId before navigating to lab list
    if (this.selectedCountry && this.countriesDetails.length > 0) {
      this.selectedCountryDetails = this.countriesDetails.find(
        c => c.geoPoliticalEntityName === this.selectedCountry
      );

      if (this.selectedCountryDetails?.geoPoliticalEntityId) {
        this.projectService.SetGeoPoliticalEntityId(this.selectedCountryDetails.geoPoliticalEntityId);
      } else {
        console.warn(MC.couldNotFindGeoPoliticalEntityIdForCountry, this.selectedCountry);
      }
    } else {
      console.warn(MC.countryOrCountriesListNotAvailable);
    }

    this.sharedService.NavigateToLabList(this.projectId, this.addProjectForm.get(PROJECT_CONSTANTS.projectNumber)?.value || '');
  }

  GoToProjectList() {
    this.sharedService.NavigateToProjectList();
  }

  onCountryChange(event: Event) {
    const selectedCountryName = (event.target as HTMLSelectElement).value;
    this.projectService.SetCountry(selectedCountryName);
    if (selectedCountryName) {
      const selectedCountry = this.countriesDetails.find(country => country.geoPoliticalEntityName === selectedCountryName);

      this._osduService.GetWellsByCountry(selectedCountry?.geoPoliticalEntityId || '').pipe(takeUntil(this.destroy$)).subscribe({
        error: (err) => {
          console.error(MC.failedToLoadWells, err);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

